import { filter } from "ramda";
import type { MultiSelectProps } from "widgets";
import { Item, MenuOptionItem, MultiSelectMenu } from "widgets";

import type { UserGroup } from "@/graphql.ts";
import { useGetUserGroupsQuery } from "@/modules/workflow/graphql/workflow.gql.ts";

interface UsergroupMultiSelectProps<T>
  extends Pick<
    MultiSelectProps<T>,
    | "label"
    | "description"
    | "aria-label"
    | "className"
    | "style"
    | "autoFocus"
    | "disabledKeys"
    | "errorMessage"
    | "isInvalid"
    | "icon"
    | "isDisabled"
    | "isRequired"
    | "id"
  > {
  onSelectionChange: (userGroupIds: UserGroup["id"][]) => void;
  selectedUserGroups: UserGroup["id"][];
  userGroupOptions?: Pick<UserGroup, "id" | "name" | "isPrivate">[];
}

const UserGroupMultiSelect = <T extends object>(props: UsergroupMultiSelectProps<T>) => {
  const hasExternallyLoadedUsergroups: boolean = props.userGroupOptions !== undefined;
  const {
    data: userGroupData,
    loading: userGroupsLoading,
    error: userGroupsError,
  } = useGetUserGroupsQuery({ skip: hasExternallyLoadedUsergroups });

  const userGroupOptions =
    props.userGroupOptions !== undefined
      ? props.userGroupOptions
      : (userGroupData?.userGroup?.accessibleUserGroups ?? []);

  // sort alphabetically, private on top
  const userGroups = filter((type): type is UserGroup => type != null, userGroupOptions)
    .map((ug) => ({
      ...ug,
      id: String(ug.id),
    }))
    .sort((ug1) => (ug1.isPrivate ? -1 : 1));

  return (
    <MultiSelectMenu
      {...props}
      selectedKeys={props.selectedUserGroups.map((s) => String(s))}
      onSelectionChange={(keys) => {
        props.onSelectionChange([...keys].map((k) => Number(k)));
      }}
      isRequired={props.isRequired}
      isDisabled={userGroupsLoading || props.isDisabled}
      placeholder={"Select user group"}
      items={userGroups}
      isInvalid={!!userGroupsError || props.isInvalid}
      errorMessage={userGroupsError?.message ?? props.errorMessage}
      selectionMode={"multiple"}
    >
      {(item) => (
        <Item key={String(item.id)} textValue={item.name}>
          <MenuOptionItem title={item.isPrivate ? "Private Workspace" : item.name} />
        </Item>
      )}
    </MultiSelectMenu>
  );
};
export default UserGroupMultiSelect;
